import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Clock, Mail, ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import authApi from "../api/authApi";

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const { verifyEmail } = useAuth();

  const [status, setStatus] = useState("verifying"); // 'verifying', 'success', 'expired', 'error'
  const [message, setMessage] = useState("");
  const [resendEmail, setResendEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("Verification token is missing from the link.");
      return;
    }

    let isMounted = true;

    const runVerification = async () => {
      try {
        const res = await verifyEmail(token);
        if (isMounted) {
          setStatus("success");
          setMessage(res?.message || "Your email address has been verified successfully!");
        }
      } catch (err) {
        if (isMounted) {
          const resData = err?.response?.data;
          const msg = resData?.message || err?.message || "Invalid or already used verification link.";
          const formattedMsg = Array.isArray(msg) ? msg.join(", ") : msg;
          const isExpired = resData?.isExpired || formattedMsg.toLowerCase().includes("expired");

          if (isExpired) {
            setStatus("expired");
            setMessage(formattedMsg || "Verification link has expired.");
          } else {
            setStatus("error");
            setMessage(formattedMsg);
          }
        }
      }
    };

    runVerification();

    return () => {
      isMounted = false;
    };
  }, [token, verifyEmail]);

  const handleResend = async (e) => {
    e.preventDefault();
    if (!resendEmail) {
      setResendMessage({ type: "error", text: "Please enter your email address." });
      return;
    }

    setResendLoading(true);
    setResendMessage({ type: "", text: "" });

    try {
      await authApi.forgotPassword({ email: resendEmail.trim().toLowerCase() });
      setResendMessage({
        type: "success",
        text: "A new activation link has been sent! Please check your email inbox.",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to resend verification email. Try again later.";
      setResendMessage({
        type: "error",
        text: Array.isArray(msg) ? msg.join(", ") : msg,
      });
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div
      style={{
        backgroundImage: "url(/images/login-bg.png)",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      className="min-h-screen w-screen flex items-center justify-center px-4 py-12 relative overflow-auto font-sans"
    >
      {/* Background Overlay */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm pointer-events-none"></div>

      <div className="w-full max-w-[520px] relative z-10 bg-black/40 backdrop-blur-md rounded-3xl p-8 md:p-10 shadow-2xl border border-white/10 text-white">
        {/* Header Branding */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 no-underline group mb-3">
            <img
              src="/images/sync_logo.png"
              alt="Sync & Explore"
              className="w-12 h-auto group-hover:scale-105 transition-transform duration-300"
            />
            <span className="text-2xl font-bold tracking-tight text-white">
              Sync & Explore
            </span>
          </Link>
          <p className="text-teal-300/80 text-sm font-medium">Email Verification</p>
        </div>

        {/* State 1: Verifying Spinner */}
        {status === "verifying" && (
          <div className="text-center py-8">
            <div className="relative w-16 h-16 mx-auto mb-6">
              <div className="absolute inset-0 rounded-full border-4 border-teal-500/20 animate-ping"></div>
              <Loader2 className="w-16 h-16 text-teal-400 animate-spin relative z-10" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">Verifying Your Email</h2>
            <p className="text-gray-300 text-sm">Please wait while we confirm your verification link...</p>
          </div>
        )}

        {/* State 2: Verification Success */}
        {status === "success" && (
          <div className="text-center py-6 animate-fadeIn">
            <div className="w-20 h-20 bg-teal-500/20 border border-teal-400/30 rounded-full flex items-center justify-center mx-auto mb-6 text-teal-400">
              <CheckCircle2 className="w-12 h-12" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">Email Verified! 🎉</h2>
            <p className="text-gray-200 text-sm leading-relaxed mb-8">{message}</p>

            <button
              onClick={() => navigate("/login")}
              className="w-full bg-teal-500 hover:bg-teal-400 text-white font-semibold py-3.5 px-6 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 shadow-lg shadow-teal-500/20 hover:scale-[1.02]"
            >
              <span>Continue to Login</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* State 3: Token Expired */}
        {status === "expired" && (
          <div className="py-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-amber-500/20 border border-amber-400/30 rounded-full flex items-center justify-center mx-auto mb-4 text-amber-400">
                <Clock className="w-9 h-9" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Verification Link Expired</h2>
              <p className="text-gray-300 text-sm">{message}</p>
            </div>

            {/* Resend Form */}
            <form
              onSubmit={handleResend}
              className="space-y-4 mt-6 bg-white/5 p-5 rounded-2xl border border-white/10"
            >
              <label className="block text-xs text-gray-300 font-medium">
                Enter your email address to receive a new verification link:
              </label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="name@example.com"
                  required
                  className="w-full bg-white/10 backdrop-blur-sm text-white placeholder-gray-400 rounded-xl py-3 pl-11 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 border border-white/10"
                />
              </div>

              {resendMessage.text && (
                <div
                  className={`text-xs p-3 rounded-lg ${
                    resendMessage.type === "success"
                      ? "bg-teal-500/20 text-teal-300 border border-teal-500/30"
                      : "bg-red-500/20 text-red-300 border border-red-500/30"
                  }`}
                >
                  {resendMessage.text}
                </div>
              )}

              <button
                type="submit"
                disabled={resendLoading}
                className="w-full bg-teal-500 hover:bg-teal-400 text-white font-semibold py-3 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {resendLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Sending Link...</span>
                  </>
                ) : (
                  <span>Resend Verification Email</span>
                )}
              </button>
            </form>

            <div className="text-center mt-6">
              <Link to="/login" className="text-xs text-gray-400 hover:text-white transition-colors">
                Back to Login
              </Link>
            </div>
          </div>
        )}

        {/* State 4: Invalid Token / Error */}
        {status === "error" && (
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-red-500/20 border border-red-400/30 rounded-full flex items-center justify-center mx-auto mb-4 text-red-400">
              <XCircle className="w-9 h-9" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Invalid Verification Link</h2>
            <p className="text-gray-300 text-sm mb-6">{message}</p>

            {/* Resend Section */}
            <form
              onSubmit={handleResend}
              className="space-y-4 mb-6 text-left bg-white/5 p-5 rounded-2xl border border-white/10"
            >
              <label className="block text-xs text-gray-300 font-medium">
                Request a new verification email:
              </label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="name@example.com"
                  required
                  className="w-full bg-white/10 backdrop-blur-sm text-white placeholder-gray-400 rounded-xl py-3 pl-11 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 border border-white/10"
                />
              </div>

              {resendMessage.text && (
                <div
                  className={`text-xs p-3 rounded-lg ${
                    resendMessage.type === "success"
                      ? "bg-teal-500/20 text-teal-300 border border-teal-500/30"
                      : "bg-red-500/20 text-red-300 border border-red-500/30"
                  }`}
                >
                  {resendMessage.text}
                </div>
              )}

              <button
                type="submit"
                disabled={resendLoading}
                className="w-full bg-teal-500 hover:bg-teal-400 text-white font-semibold py-3 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {resendLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Sending...</span>
                  </>
                ) : (
                  <span>Send Verification Email</span>
                )}
              </button>
            </form>

            <Link
              to="/login"
              className="inline-block text-sm text-teal-300 hover:text-white transition-colors"
            >
              Return to Login
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
